import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../helpers/AuthContext';
import Alert from '../Alert';

const SCOPES = [
    {
        key: 'storefront',
        label: 'Storefront CSS',
        icon: 'fa-solid fa-store',
        description: 'Applies across the storefront.',
    },
    {
        key: 'blog',
        label: 'Blog CSS',
        icon: 'fa-solid fa-blog',
        description: 'Adds styling on blog listing and blog post pages.',
    },
    {
        key: 'page',
        label: 'Page CSS',
        icon: 'fa-solid fa-file-lines',
        description: 'Adds styling on custom pages.',
    },
];

const MAX_CUSTOM_CSS_LENGTH = 20000;
const FORBIDDEN_PATTERNS = [
    { pattern: /<\/?\s*style\b/i, message: 'CSS cannot contain style tags.' },
    { pattern: /<\s*script\b/i, message: 'CSS cannot contain script tags.' },
    { pattern: /<!--|-->/, message: 'CSS cannot contain HTML comments.' },
    { pattern: /@import\b/i, message: 'CSS cannot import external stylesheets.' },
    { pattern: /url\s*\(/i, message: 'CSS cannot reference external URLs.' },
    { pattern: /expression\s*\(/i, message: 'CSS cannot use expression().' },
    { pattern: /javascript\s*:/i, message: 'CSS cannot contain javascript: URLs.' },
    { pattern: /vbscript\s*:/i, message: 'CSS cannot contain vbscript: URLs.' },
    { pattern: /data\s*:/i, message: 'CSS cannot contain data: URLs.' },
    { pattern: /behavior\s*:/i, message: 'CSS cannot use behavior.' },
    { pattern: /-moz-binding\s*:/i, message: 'CSS cannot use -moz-binding.' },
];

function stripCssComments(css) {
    return css.replace(/\/\*[\s\S]*?\*\//g, '');
}

function hasBalancedBraces(css) {
    let depth = 0;
    for (const char of css) {
        if (char === '{') depth += 1;
        if (char === '}') depth -= 1;
        if (depth < 0) return false;
    }
    return depth === 0;
}

function browserCanParseCss(css) {
    const stripped = stripCssComments(css).trim();
    if (!stripped) return true;

    if (typeof CSSStyleSheet !== 'undefined') {
        try {
            const sheet = new CSSStyleSheet();
            if (typeof sheet.replaceSync === 'function') {
                sheet.replaceSync(css);
                return sheet.cssRules.length > 0;
            }
        } catch {
            return false;
        }
    }

    try {
        const documentForParsing = document.implementation.createHTMLDocument('');
        const style = documentForParsing.createElement('style');
        style.textContent = css;
        documentForParsing.head.appendChild(style);
        return (style.sheet?.cssRules?.length || 0) > 0;
    } catch {
        return false;
    }
}

function validateCss(css) {
    const errors = [];

    if (css.length > MAX_CUSTOM_CSS_LENGTH) {
        errors.push(`CSS must be ${MAX_CUSTOM_CSS_LENGTH} characters or fewer.`);
    }

    FORBIDDEN_PATTERNS.forEach(rule => {
        if (rule.pattern.test(css)) errors.push(rule.message);
    });

    const withoutComments = stripCssComments(css);
    if (!hasBalancedBraces(withoutComments)) {
        errors.push('CSS has unbalanced braces.');
    }

    if (withoutComments.trim() && !browserCanParseCss(css)) {
        errors.push('CSS could not be parsed.');
    }

    return errors;
}

export default function CustomCssManager() {
    const { fetch, API_URL } = useAuth();
    const [activeScope, setActiveScope] = useState('storefront');
    const [customCss, setCustomCss] = useState({ storefront: '', blog: '', page: '' });
    const [drafts, setDrafts] = useState({ storefront: '', blog: '', page: '' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetch(`${API_URL}/v1/seller/branding/custom-css`, {
            headers: { Accept: 'application/json' },
        })
            .then(async response => {
                const payload = await response.json();
                if (!response.ok) throw new Error(payload.error || 'Failed to load custom CSS');
                if (cancelled) return;
                const data = {
                    storefront: payload.data?.storefront || '',
                    blog: payload.data?.blog || '',
                    page: payload.data?.page || '',
                };
                setCustomCss(data);
                setDrafts(data);
                setError('');
            })
            .catch(err => {
                if (!cancelled) setError(err.message || 'Failed to load custom CSS');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const activeConfig = useMemo(
        () => SCOPES.find(scope => scope.key === activeScope) || SCOPES[0],
        [activeScope]
    );
    const currentDraft = drafts[activeScope] || '';
    const validationErrors = validateCss(currentDraft);
    const isDirty = currentDraft !== (customCss[activeScope] || '');

    async function saveCurrentScope() {
        const errors = validateCss(currentDraft);
        if (errors.length > 0) {
            setError(errors[0]);
            setSuccess('');
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(`${API_URL}/v1/seller/branding/custom-css/${activeScope}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ css: currentDraft }),
            });
            const payload = await response.json();
            if (!response.ok) throw new Error(payload.error || 'Failed to save custom CSS');
            const data = payload.data?.customCss || { ...customCss, [activeScope]: payload.data?.css || '' };
            setCustomCss(data);
            setDrafts(data);
            setSuccess(`${activeConfig.label} saved.`);
        } catch (err) {
            setError(err.message || 'Failed to save custom CSS');
        } finally {
            setSaving(false);
        }
    }

    async function deleteCurrentScope() {
        if (!window.confirm(`Delete ${activeConfig.label}?`)) return;

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(`${API_URL}/v1/seller/branding/custom-css/${activeScope}`, {
                method: 'DELETE',
                headers: { Accept: 'application/json' },
            });
            const payload = await response.json();
            if (!response.ok) throw new Error(payload.error || 'Failed to delete custom CSS');
            const data = payload.data?.customCss || { ...customCss, [activeScope]: '' };
            setCustomCss(data);
            setDrafts(data);
            setSuccess(`${activeConfig.label} deleted.`);
        } catch (err) {
            setError(err.message || 'Failed to delete custom CSS');
        } finally {
            setSaving(false);
        }
    }

    function updateDraft(value) {
        setDrafts(prev => ({ ...prev, [activeScope]: value }));
        setError('');
        setSuccess('');
    }

    return (
        <div className="custom-css-manager">
            {error ? <Alert type="error">{error}</Alert> : null}
            {success ? <Alert type="success">{success}</Alert> : null}
            {loading ? (
                <p className="help">Loading custom CSS...</p>
            ) : (
                <>
                    <div className="group">
                        <label htmlFor="custom-css-scope">CSS Area</label>
                        <select
                            id="custom-css-scope"
                            value={activeScope}
                            onChange={event => {
                                setActiveScope(event.target.value);
                                setError('');
                                setSuccess('');
                            }}
                        >
                            {SCOPES.map(scope => (
                                <option key={scope.key} value={scope.key}>
                                    {scope.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="group">
                        <label htmlFor="custom-css-editor">{activeConfig.label}</label>
                        <textarea
                            id="custom-css-editor"
                            value={currentDraft}
                            rows={14}
                            spellCheck="false"
                            onChange={event => updateDraft(event.target.value)}
                            placeholder=".storefront header h1 { color: #316C29; }"
                            style={{ fontFamily: 'monospace', minHeight: '260px' }}
                        />
                        <p className="help">{activeConfig.description}</p>
                        {validationErrors.length > 0 ? (
                            <div className="error">
                                {validationErrors[0]}
                            </div>
                        ) : null}
                    </div>

                    <div className="group">
                        <button
                            type="button"
                            className="button primary"
                            disabled={saving || validationErrors.length > 0 || !isDirty}
                            onClick={saveCurrentScope}
                        >
                            {saving ? 'Saving...' : 'Save CSS'}
                        </button>
                        <button
                            type="button"
                            className="button danger"
                            disabled={saving || !(customCss[activeScope] || '')}
                            onClick={deleteCurrentScope}
                        >
                            <i className="fa-solid fa-trash-can"></i> Delete
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
